import type { DaySnapshot } from '../api/account-timeline';

/**
 * Точки «Хронологии» по месяцам.
 *
 * Сервер отдаёт по одной точке на месяц, но тип исторически дневной, поэтому
 * потоки (показы, охваты, взаимодействия, новые подписчики) складываются, а
 * подписчики на конец месяца — уровень: берётся значение самой поздней точки.
 */
export type MonthPoint = {
  month: string; // 'YYYY-MM'
  label: string;
  impressions: number | null;
  reach: number | null;
  interactions: number | null;
  follower_delta: number | null;
  followers: number | null;
};

function addNullable(a: number | null, b: number | null | undefined): number | null {
  if (b == null) return a;
  return (a ?? 0) + b;
}

export function formatMonthLabel(month: string): string {
  const [year, m] = month.split('-');
  const d = new Date(Number(year), Number(m) - 1, 1);
  return d.toLocaleDateString('ru-RU', { month: 'short', year: '2-digit' });
}

export function aggregateByMonth(days: DaySnapshot[]): MonthPoint[] {
  const map = new Map<string, MonthPoint & { lastDate: string }>();
  for (const day of days) {
    const month = day.date.slice(0, 7);
    let pt = map.get(month);
    if (!pt) {
      pt = {
        month,
        label: formatMonthLabel(month),
        impressions: null,
        reach: null,
        interactions: null,
        follower_delta: null,
        followers: null,
        lastDate: '',
      };
      map.set(month, pt);
    }
    pt.impressions = addNullable(pt.impressions, day.impressions);
    pt.reach = addNullable(pt.reach, day.reach);
    pt.interactions = addNullable(pt.interactions, day.interactions);
    pt.follower_delta = addNullable(pt.follower_delta, day.follower_delta);
    if (day.followers != null && day.date >= pt.lastDate) {
      pt.followers = day.followers;
      pt.lastDate = day.date;
    }
  }
  return Array.from(map.values())
    .sort((a, b) => a.month.localeCompare(b.month))
    .map(({ lastDate: _lastDate, ...pt }) => pt);
}

/** Тот же месяц год назад: '2026-09' → '2025-09'. */
export function yearAgoMonth(month: string): string {
  const [y, m] = month.split('-');
  return `${Number(y) - 1}-${m}`;
}

/** Изменение к тому же месяцу прошлого года в процентах; null — сравнивать не с чем. */
export function yearOverYear(current: number | null, previous: number | null | undefined): number | null {
  if (current == null || previous == null || previous === 0) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}
